
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  CheckCheck,
  Check,
  Clock,
  Users,
  ShieldCheck,
  Info,
  RefreshCw,
  Inbox,
} from "lucide-react";
import "./PartnerNotifications.css";

const API_URL = "/api/notifications";

const PartnerNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);

    setError("");

    try {
      const response = await fetch(`${API_URL}/admin/partners`);

      if (!response.ok) {
        throw new Error(
          `Failed to load notifications (${response.status}).`
        );
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Unexpected notification response from server.");
      }

      setNotifications(data);
    } catch (err) {
      setError(
        err.message ||
          "Could not connect to the backend. Check that Spring Boot is running."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(
    (notification) => !notification.isRead
  ).length;

  // A new partner registration needs admin attention until read.
  const actionRequiredCount = notifications.filter(
    (notification) =>
      !notification.isRead &&
      notification.notificationType === "PARTNER_REGISTERED"
  ).length;

  const filteredNotifications = useMemo(() => {
    if (activeFilter === "unread") {
      return notifications.filter(
        (notification) => !notification.isRead
      );
    }

    if (activeFilter === "action") {
      return notifications.filter(
        (notification) =>
          !notification.isRead &&
          notification.notificationType === "PARTNER_REGISTERED"
      );
    }

    return notifications;
  }, [notifications, activeFilter]);

  const refreshNotifications = async () => {
    setRefreshing(true);

    try {
      await fetchNotifications(false);
    } finally {
      setRefreshing(false);
    }
  };

  const markAsRead = async (id) => {
    setProcessingId(id);
    setError("");

    try {
      const response = await fetch(`${API_URL}/${id}/read`, {
        method: "PUT",
      });

      if (!response.ok) {
        throw new Error("Could not mark notification as read.");
      }

      const updatedNotification = await response.json();

      setNotifications((previous) =>
        previous.map((notification) =>
          notification.id === id
            ? { ...notification, ...updatedNotification }
            : notification
        )
      );
    } catch (err) {
      setError(err.message || "Unable to update notification.");
    } finally {
      setProcessingId(null);
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.isRead
    );

    if (unreadNotifications.length === 0) return;

    setMarkingAll(true);
    setError("");

    try {
      // Use the existing mark-one-as-read endpoint.
      // This avoids needing the admin user ID for read-all.
      const results = await Promise.all(
        unreadNotifications.map(async (notification) => {
          const response = await fetch(
            `${API_URL}/${notification.id}/read`,
            { method: "PUT" }
          );

          if (!response.ok) {
            throw new Error(
              `Could not mark notification ${notification.id} as read.`
            );
          }

          return response.json();
        })
      );

      const updatedById = new Map(
        results.map((notification) => [notification.id, notification])
      );

      setNotifications((previous) =>
        previous.map(
          (notification) =>
            updatedById.get(notification.id) || notification
        )
      );
    } catch (err) {
      setError(
        `${err.message || "Some notifications could not be updated."} Refresh the page to check their status.`
      );

      await fetchNotifications(false);
    } finally {
      setMarkingAll(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "PARTNER_REGISTERED":
      case "PARTNER_APPROVED":
      case "PARTNER_REJECTED":
        return <Users size={20} />;

      case "TRAINER_REGISTERED":
      case "TRAINER_APPROVED":
      case "TRAINER_REJECTED":
        return <ShieldCheck size={20} />;

      default:
        return <Info size={20} />;
    }
  };

  const formatDate = (date) => {
    if (!date) return "Date unavailable";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "Date unavailable";
    }

    return parsedDate.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <div className="admin-partner-notifications">
      <div className="pn-page-header">
        <div className="pn-heading">
          <div className="pn-heading-icon">
            <Bell size={25} />
          </div>

          <div>
            <h1>Partner Notifications</h1>
            <p>
              Monitor partner registrations, verification requests,
              and important updates.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="pn-refresh-button"
          onClick={refreshNotifications}
          disabled={refreshing || loading}
          title="Refresh notifications"
        >
          <RefreshCw
            size={17}
            className={refreshing ? "pn-spinning" : ""}
          />
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="pn-error-message" role="alert">
          {error}
        </div>
      )}

      <div className="pn-stats-grid">
        <div className="pn-stat-card">
          <div className="pn-stat-icon pn-icon-purple">
            <Bell size={21} />
          </div>
          <div>
            <span>Total notifications</span>
            <h2>{notifications.length}</h2>
          </div>
        </div>

        <div className="pn-stat-card">
          <div className="pn-stat-icon pn-icon-gold">
            <Clock size={21} />
          </div>
          <div>
            <span>Unread</span>
            <h2>{unreadCount}</h2>
          </div>
        </div>

        <div className="pn-stat-card">
          <div className="pn-stat-icon pn-icon-blue">
            <ShieldCheck size={21} />
          </div>
          <div>
            <span>Action required</span>
            <h2>{actionRequiredCount}</h2>
          </div>
        </div>
      </div>

      <section className="pn-notifications-panel">
        <div className="pn-panel-header">
          <div>
            <h2>All notifications</h2>
            <p>Stay updated on partner-related activity.</p>
          </div>

          <button
            type="button"
            className="pn-mark-all-button"
            onClick={markAllAsRead}
            disabled={unreadCount === 0 || markingAll}
          >
            <CheckCheck size={17} />
            {markingAll ? "Updating..." : "Mark all as read"}
          </button>
        </div>

        <div className="pn-filter-row">
          <button
            type="button"
            className={activeFilter === "all" ? "active" : ""}
            onClick={() => setActiveFilter("all")}
          >
            All
            <span>{notifications.length}</span>
          </button>

          <button
            type="button"
            className={activeFilter === "unread" ? "active" : ""}
            onClick={() => setActiveFilter("unread")}
          >
            Unread
            <span>{unreadCount}</span>
          </button>

          <button
            type="button"
            className={activeFilter === "action" ? "active" : ""}
            onClick={() => setActiveFilter("action")}
          >
            Action required
            <span>{actionRequiredCount}</span>
          </button>
        </div>

        <div className="pn-notification-list">
          {loading ? (
            <div className="pn-empty-state">
              <RefreshCw size={28} />
              <h3>Loading notifications...</h3>
              <p>Please wait while we fetch updates from the server.</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="pn-empty-state">
              <div className="pn-empty-icon">
                <Inbox size={34} />
              </div>

              <h3>
                {activeFilter === "unread"
                  ? "No unread notifications"
                  : activeFilter === "action"
                  ? "No actions pending"
                  : "No notifications yet"}
              </h3>

              <p>
                {activeFilter === "all"
                  ? "Partner updates will appear here when the backend creates notifications."
                  : "There are no notifications matching this filter."}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const needsAction =
                !notification.isRead &&
                notification.notificationType === "PARTNER_REGISTERED";

              return (
                <article
                  className={`pn-notification-item ${
                    !notification.isRead ? "pn-unread" : ""
                  }`}
                  key={notification.id}
                >
                  <div className="pn-notification-icon">
                    {getNotificationIcon(notification.notificationType)}
                  </div>

                  <div className="pn-notification-content">
                    <div className="pn-notification-title-row">
                      <h3>{notification.title}</h3>
                      {!notification.isRead && (
                        <span className="pn-unread-dot" />
                      )}
                    </div>

                    <p>{notification.message}</p>

                    <span className="pn-notification-date">
                      {formatDate(notification.createdAt)}
                    </span>
                  </div>

                  <div className="pn-notification-actions">
                    {needsAction && (
                      <span className="pn-action-badge">
                        Action required
                      </span>
                    )}

                    {!notification.isRead && (
                      <button
                        type="button"
                        className="pn-read-button"
                        onClick={() => markAsRead(notification.id)}
                        disabled={processingId === notification.id}
                      >
                        <Check size={15} />
                        {processingId === notification.id
                          ? "Updating..."
                          : "Mark read"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>

        <div className="pn-panel-footer">
          Showing {filteredNotifications.length} of{" "}
          {notifications.length} notifications
        </div>
      </section>
    </div>
  );
};

export default PartnerNotifications;
